import React, { useState, useEffect } from 'react';
// Types
import {
  CbDeliverableV2Status,
  TaskExtensionRequestStatus,
  TaskExtensionRequestType,
} from 'api/graphql-global-types';
import {
  GetCampaignOffers_getCampaignOffers_entities,
  GetCampaignOffers_getCampaignOffers_entities_deliverablesV2,
} from 'api/campaignV2/types/GetCampaignOffers';
import { GetCbPendingExtensionRequests_getCbPendingExtensionRequests } from 'api/campaignV2/types/GetCbPendingExtensionRequests';
// Hooks
import { useTaskExtensions } from 'hooks/useTaskExtensions';
// Constants
import { DONE_DELIVERABLE_STATUSES } from 'constants/deliverables';
// Helpers
import {
  deliverableHasContentAction,
  getDeliverableKind,
  getDeliverablesTimelineCards,
} from 'helpers/deliverables';
import { checkPublishUploadAllowed } from 'helpers/timelineDeliverables';
import {
  isCustomLikeCampaign,
  isOnlyContentDeliverables,
} from 'helpers/campaign';
// Components
import DeliverablePublishCard from 'components/common/SocialCampaignV2Components/DeliverablePublishCard/DeliverablePublishCard';
import DeliverableContentCard from 'components/common/SocialCampaignV2Components/DeliverableContentCard/DeliverableContentCard';
import DeliverableAppearanceCard from 'components/common/SocialCampaignV2Components/DeliverableAppearanceCard/DeliverableAppearanceCard';
import VerticalStepper, {
  VerticalStepperNodeStep,
} from '../VerticalStepper/VerticalStepper';
import SubmitContentForm from '../SubmitContentForm/SubmitContentForm';
import DeliverableSubmitted from '../DeliverableSubmitted/DeliverableSubmitted';
import SubmitPostForm from '../SubmitPostForm/SubmitPostForm';
import ReviewExtensionModal from 'pages/CampaignDetails/CampaignDetails/components/TalentTable/components/CampaignDeliverablesReview/CampaignDeliverablesSubmitForReviewForm/components/SubmitTimelineDeliverables/components/ReviewExtensionModal/ReviewExtensionModal';
import RequestExtensionModal from 'pages/CampaignDetails/CampaignDetails/components/TalentTable/components/CampaignDeliverablesReview/CampaignDeliverablesSubmitForReviewForm/components/SubmitTimelineDeliverables/components/RequestExtensionModal/RequestExtensionModal';

type DeliverablesTimelineProps = {
  offer?: GetCampaignOffers_getCampaignOffers_entities | null;
  refetch?: () => void;
};

const getSteps = (
  offer?: GetCampaignOffers_getCampaignOffers_entities | null,
  extensionProps?: {
    hasPendingRequest: (deliverableId: string) => boolean;
    onRequestExtension: (
      deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
    ) => void;
    onReviewExtension: (
      deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
    ) => void;
  },
  refetch?: () => void
): { steps: VerticalStepperNodeStep[]; activeStep: number } => {
  const { contentDeliverables, publishDeliverables } =
    getDeliverablesTimelineCards(
      offer as GetCampaignOffers_getCampaignOffers_entities
    );

  let activeStep = 0;
  const steps: VerticalStepperNodeStep[] = [];
  const isContentOnlyType = isOnlyContentDeliverables(offer?.campaign?.type);
  const supportsMixedDeliverables = isCustomLikeCampaign(offer?.campaign?.type);

  // Mixed-deliverable campaigns give appearance tasks their own confirmation
  // content/post phase — they get their own confirmation step below. UGC/social
  // keep the standard content (+ post) flow. Other campaign types are untouched.
  const contentDeliverablesToRender = supportsMixedDeliverables
    ? contentDeliverables.filter(
        (deliverable) => getDeliverableKind(deliverable) !== 'appearance'
      )
    : contentDeliverables;

  // Content Deliverables
  contentDeliverablesToRender.forEach((deliverable) => {
    const isDone =
      deliverable.status !== undefined &&
      DONE_DELIVERABLE_STATUSES.includes(deliverable.status);

    if (isDone) {
      activeStep = activeStep + 1;
    }

    steps.push({
      type: 'content' as const,
      node: (
        <DeliverableContentCard
          deliverable={deliverable}
          key={`content-${deliverable.id}`}
          campaignType={offer?.campaign?.type}
        >
          {isDone ? (
            <DeliverableSubmitted
              deliverable={
                deliverable as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
              }
              defaultExpanded={false}
              hasPendingRequest={extensionProps?.hasPendingRequest}
              onRequestExtension={extensionProps?.onRequestExtension}
              onReviewExtension={extensionProps?.onReviewExtension}
            />
          ) : (
            <SubmitContentForm
              offer={offer}
              deliverable={deliverable}
              hasPendingRequest={extensionProps?.hasPendingRequest}
              onRequestExtension={extensionProps?.onRequestExtension}
              onReviewExtension={extensionProps?.onReviewExtension}
              refetch={refetch}
            />
          )}
        </DeliverableContentCard>
      ),
    });
  });

  if (isContentOnlyType) {
    return { steps, activeStep };
  }

  // For mixed-deliverable campaigns only social deliverables have a publish phase;
  // UGC is upload-only and appearance has its own confirmation step. Other
  // campaign types keep their original behaviour unchanged.
  const publishDeliverablesToRender = supportsMixedDeliverables
    ? publishDeliverables.filter(
        (deliverable) => getDeliverableKind(deliverable) === 'social'
      )
    : publishDeliverables;

  // Publish Deliverables
  publishDeliverablesToRender.forEach((deliverable) => {
    const isPublished = deliverable.status === CbDeliverableV2Status.PUBLISHED;
    const isUploadAllowed = checkPublishUploadAllowed(deliverable);
    if (isPublished) {
      activeStep = activeStep + 1;
    }

    steps.push({
      type: 'post' as const,
      node: (
        <DeliverablePublishCard
          deliverable={deliverable}
          key={`post-${deliverable.id}`}
          isUploadAllowed={isUploadAllowed}
        >
          {isUploadAllowed ? (
            <SubmitPostForm
              offer={offer}
              deliverable={deliverable}
              hasPendingRequest={extensionProps?.hasPendingRequest}
              onRequestExtension={extensionProps?.onRequestExtension}
              onReviewExtension={extensionProps?.onReviewExtension}
            />
          ) : isPublished ? (
            <DeliverableSubmitted
              deliverable={
                deliverable as GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
              }
              submissions={offer?.submissions}
              defaultExpanded={false}
              hasPendingRequest={extensionProps?.hasPendingRequest}
              onRequestExtension={extensionProps?.onRequestExtension}
              onReviewExtension={extensionProps?.onReviewExtension}
            />
          ) : null}
        </DeliverablePublishCard>
      ),
    });
  });

  // Appearance deliverables have no content/post phase, only a single
  // card with a "Mark as Complete" action that unlocks on/after the scheduled
  // appearance date.
  if (supportsMixedDeliverables) {
    const appearanceDeliverables = publishDeliverables.filter(
      (deliverable) => getDeliverableKind(deliverable) === 'appearance'
    );

    appearanceDeliverables.forEach((deliverable) => {
      const isDone = deliverable.status === CbDeliverableV2Status.APPROVED;
      if (isDone) {
        activeStep = activeStep + 1;
      }

      steps.push({
        type: 'appearance' as const,
        node: (
          <DeliverableAppearanceCard
            deliverable={deliverable}
            key={`appearance-${deliverable.id}`}
            offerId={offer?.id ?? ''}
            campaignId={offer?.campaign?.id ?? ''}
            refetch={refetch}
            hasPendingRequest={extensionProps?.hasPendingRequest}
            onRequestExtension={extensionProps?.onRequestExtension}
            onReviewExtension={extensionProps?.onReviewExtension}
          />
        ),
      });
    });
  }

  return { steps, activeStep };
};

const DeliverablesSubmitTimeline: React.FC<DeliverablesTimelineProps> = ({
  offer,
  refetch,
}) => {
  const [showRequestExtensionModal, setShowRequestExtensionModal] =
    useState(false);
  const [showReviewExtensionModal, setShowReviewExtensionModal] =
    useState(false);
  const [selectedDeliverable, setSelectedDeliverable] =
    useState<GetCampaignOffers_getCampaignOffers_entities_deliverablesV2 | null>(
      null
    );
  const [pendingExtensionRequest, setPendingExtensionRequest] =
    useState<GetCbPendingExtensionRequests_getCbPendingExtensionRequests | null>(
      null
    );
  const [extensionType, setExtensionType] =
    useState<TaskExtensionRequestType | null>(null);

  const {
    getCbTaskExtensionHistory: getCbTaskExtensionHistoryQuery,
    getPendingTaskExtensionRequests,
    extensionHistoryData,
    extensionHistoryLoading,
    pendingTaskExtensionRequestsData,
  } = useTaskExtensions();

  // Check if there's a pending extension request for a deliverable
  const hasPendingRequest = (deliverableId: string) => {
    if (!pendingTaskExtensionRequestsData?.getCbPendingExtensionRequests) {
      return false;
    }
    return pendingTaskExtensionRequestsData.getCbPendingExtensionRequests.some(
      (request) => request.deliverableId === deliverableId
    );
  };

  const handleRequestExtension = async (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => {
    await getCbTaskExtensionHistoryQuery({
      variables: {
        input: {
          offerId: offer?.id || '',
        },
      },
    });

    // Appearance deliverables are event-based: they extend the scheduled event
    // date (EVENT) and carry no content/post id suffix.
    if (getDeliverableKind(deliverable) === 'appearance') {
      setExtensionType(TaskExtensionRequestType.EVENT);
      setSelectedDeliverable(deliverable);
      setShowRequestExtensionModal(true);
      return;
    }

    const contentExtensionRequest =
      extensionHistoryData?.getAgencyCbTaskExtensionHistory?.find(
        (h) =>
          h.deliverable.id === deliverable.id &&
          h.type === TaskExtensionRequestType.CONTENT
      );
    const postExtensionRequest =
      extensionHistoryData?.getAgencyCbTaskExtensionHistory?.find(
        (h) =>
          h.deliverable.id === deliverable.id &&
          h.type === TaskExtensionRequestType.PUBLISH
      );

    if (
      deliverableHasContentAction(deliverable.status) &&
      (!contentExtensionRequest ||
        (contentExtensionRequest.status ===
          TaskExtensionRequestStatus.APPROVED &&
          contentExtensionRequest.type === TaskExtensionRequestType.CONTENT))
    ) {
      setExtensionType(TaskExtensionRequestType.CONTENT);
    } else if (
      !offer?.submissions?.find((s) => s.task?.id === deliverable.task?.id) &&
      (!postExtensionRequest ||
        (postExtensionRequest.status === TaskExtensionRequestStatus.APPROVED &&
          postExtensionRequest.type === TaskExtensionRequestType.PUBLISH))
    ) {
      setExtensionType(TaskExtensionRequestType.PUBLISH);
    }

    // Use original deliverable ID for extension requests
    const originalDeliverable = {
      ...deliverable,
      id:
        (deliverable as any).originalId ||
        deliverable.id.replace(/-content$|-post$/, ''),
    };
    setSelectedDeliverable(originalDeliverable);
    setShowRequestExtensionModal(true);
  };

  const handleReviewExtension = (
    deliverable: GetCampaignOffers_getCampaignOffers_entities_deliverablesV2
  ) => {
    const isAppearance = getDeliverableKind(deliverable) === 'appearance';

    if (isAppearance) {
      setExtensionType(TaskExtensionRequestType.EVENT);
    } else if (deliverable.id.includes('-content')) {
      setExtensionType(TaskExtensionRequestType.CONTENT);
    } else {
      setExtensionType(TaskExtensionRequestType.PUBLISH);
    }

    // Use original deliverable ID for extension requests. Appearance ids carry
    // no content/post suffix, so keep them as-is.
    const originalDeliverable = {
      ...deliverable,
      id: isAppearance
        ? deliverable.id
        : (deliverable as any).originalId ||
          deliverable.id.replace(/-content$|-post$/, ''),
    };

    // Find the pending request for this deliverable immediately
    if (pendingTaskExtensionRequestsData?.getCbPendingExtensionRequests) {
      const requests =
        pendingTaskExtensionRequestsData.getCbPendingExtensionRequests;
      const requestForThisDeliverable = requests.find(
        (request) => request.deliverableId === originalDeliverable.id
      );

      // Set all states at once
      setSelectedDeliverable(originalDeliverable);
      setPendingExtensionRequest(requestForThisDeliverable || null);
      setShowReviewExtensionModal(true);
    }
  };

  const handleExtensionSuccess = () => {
    if (offer) {
      getPendingTaskExtensionRequests({
        variables: {
          input: {
            offerId: offer.id,
          },
        },
      });
    }

    setSelectedDeliverable(null);
    setPendingExtensionRequest(null);
  };

  // Execute the query when component mounts
  useEffect(() => {
    if (offer) {
      getPendingTaskExtensionRequests({
        variables: {
          input: {
            offerId: offer.id,
          },
        },
      });
    }
  }, [offer, getPendingTaskExtensionRequests]);

  const extensionProps = {
    hasPendingRequest,
    onRequestExtension: handleRequestExtension,
    onReviewExtension: handleReviewExtension,
  };

  const { steps, activeStep } = getSteps(offer, extensionProps, refetch);

  return (
    <>
      <VerticalStepper nodeSteps={steps} activeStep={activeStep} />

      {/* Extension Modals */}
      {selectedDeliverable && extensionType && (
        <RequestExtensionModal
          extensionType={extensionType}
          isVisible={showRequestExtensionModal}
          onClose={() => {
            setShowRequestExtensionModal(false);
            setSelectedDeliverable(null);
          }}
          deliverable={selectedDeliverable}
          onSuccess={handleExtensionSuccess}
          offerId={offer?.id || ''}
          getCbTaskExtensionHistory={getCbTaskExtensionHistoryQuery}
          extensionHistoryData={
            extensionHistoryData?.getAgencyCbTaskExtensionHistory
          }
          extensionHistoryLoading={extensionHistoryLoading}
        />
      )}

      {showReviewExtensionModal && pendingExtensionRequest && offer && (
        <ReviewExtensionModal
          isVisible={showReviewExtensionModal}
          onClose={() => {
            setShowReviewExtensionModal(false);
            setSelectedDeliverable(null);
            setPendingExtensionRequest(null);
          }}
          request={pendingExtensionRequest}
          offer={offer}
          onSuccess={handleExtensionSuccess}
          getCbTaskExtensionHistory={getCbTaskExtensionHistoryQuery}
          extensionHistoryData={
            extensionHistoryData?.getAgencyCbTaskExtensionHistory
          }
          extensionHistoryLoading={extensionHistoryLoading}
        />
      )}
    </>
  );
};

export default DeliverablesSubmitTimeline;
